import React, { useState } from 'react';
import { X } from 'lucide-react';
import api from '../../../services/api';
import alertService from '../../../utils/alert';
import { cn } from '../../../utils/cn';
import { formatCurrency } from '../../../utils/currency';

const INPUT = 'w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3]';
const LABEL = 'block text-[11px] font-semibold text-[#706E6B] mb-1';
const STATUS = {
  paid: 'bg-[#DCFCE7] text-[#15803D]', partial: 'bg-[#FEF3C7] text-[#92400E]',
  overdue: 'bg-[#FEE2E2] text-[#B91C1C]', pending: 'bg-[#F3F2F2] text-[#444444]',
};
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const nextMonth = () => { const d = new Date(); d.setMonth(d.getMonth() + 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

// View or set up an EMI schedule. Payments fill instalments in date order
// automatically, whichever way they're recorded.
export default function InstallmentModal({ invoice, onClose, onSaved }) {
  const has = invoice.installments?.length > 0;
  const [editing, setEditing] = useState(!has);
  const [form, setForm] = useState({ count: 3, frequency: 'monthly', firstDueDate: nextMonth() });
  const [saving, setSaving] = useState(false);
  const c = invoice.currency;
  const each = Math.floor(((invoice.totalAmount || 0) / (Number(form.count) || 1)) * 100) / 100;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/crm/invoices/${invoice._id}/installments`, { count: Number(form.count), frequency: form.frequency, firstDueDate: form.firstDueDate });
      alertService.success('Instalment plan saved');
      onSaved(data.invoice);
    } catch (e) {
      alertService.error('Could not save plan', e.response?.data?.message || 'Please check the details');
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!(await alertService.confirm('Remove instalment plan?', 'The invoice goes back to a single due date.', 'danger', 'Remove'))) return;
    try {
      const { data } = await api.delete(`/crm/invoices/${invoice._id}/installments`);
      onSaved(data.invoice);
    } catch (e) {
      alertService.error('Could not remove plan', e.response?.data?.message || '');
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-end sm:items-start justify-center overflow-y-auto sm:p-4" onMouseDown={onClose}>
      <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-xl sm:my-8" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
          <h2 className="text-[16px] font-bold text-[#2E2E2E]">Instalment plan · {invoice.invoiceNumber}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-[#F3F2F2]"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-[13px] text-[#706E6B]">Invoice total <strong className="text-[#2E2E2E] tabular-nums">{formatCurrency(invoice.totalAmount, c)}</strong> · paid <strong className="text-[#2E2E2E] tabular-nums">{formatCurrency(invoice.amountPaid || 0, c)}</strong></p>

          {has && !editing && (
            <div className="border border-[#D8DDE6] rounded overflow-hidden">
              <table className="w-full text-[13px]">
                <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr>
                  <th className="text-left font-semibold px-3 py-2">#</th><th className="text-left font-semibold px-3 py-2">Due</th>
                  <th className="text-right font-semibold px-3 py-2">Amount</th><th className="text-right font-semibold px-3 py-2">Paid</th>
                  <th className="text-left font-semibold px-3 py-2">Status</th>
                </tr></thead>
                <tbody>
                  {invoice.installments.map((i, idx) => (
                    <tr key={i._id || idx} className="border-t border-[#F3F2F2]">
                      <td className="px-3 py-2 text-[#706E6B]">{idx + 1}</td>
                      <td className="px-3 py-2">{fmtDate(i.dueDate)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(i.amount, c)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(i.paidAmount || 0, c)}</td>
                      <td className="px-3 py-2"><span className={cn('px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize', STATUS[i.status] || STATUS.pending)}>{i.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {editing && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><label className={LABEL} htmlFor="emi-count">Instalments</label><input id="emi-count" type="number" min="2" max="60" className={INPUT} value={form.count} onChange={(e) => setForm({ ...form, count: e.target.value })} /></div>
              <div>
                <label className={LABEL} htmlFor="emi-freq">Every</label>
                <select id="emi-freq" className={INPUT} value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}>
                  <option value="weekly">Week</option><option value="biweekly">2 weeks</option><option value="monthly">Month</option><option value="quarterly">Quarter</option>
                </select>
              </div>
              <div><label className={LABEL} htmlFor="emi-first">First due</label><input id="emi-first" type="date" className={INPUT} value={form.firstDueDate} onChange={(e) => setForm({ ...form, firstDueDate: e.target.value })} /></div>
              <p className="sm:col-span-3 text-[12px] text-[#706E6B]">About <strong className="text-[#2E2E2E] tabular-nums">{formatCurrency(each, c)}</strong> each; the last instalment absorbs any paisa difference. Payments already received count toward the earliest instalments.</p>
            </div>
          )}
        </div>
        <div className="flex justify-between gap-2 px-5 py-3 border-t border-[#D8DDE6] bg-[#FAFAF9] rounded-b-lg">
          <div>{has && <button type="button" onClick={remove} className="h-9 px-3 rounded text-[13px] font-semibold text-[#BA0517]">Remove plan</button>}</div>
          <div className="flex gap-2">
            {editing && has && <button type="button" onClick={() => setEditing(false)} className="h-9 px-4 rounded border border-[#C9C9C9] text-[13px] font-semibold bg-white">Back</button>}
            {!editing && <button type="button" onClick={() => setEditing(true)} className="h-9 px-4 rounded border border-[#C9C9C9] text-[13px] font-semibold bg-white">Change plan</button>}
            {editing && <button type="button" disabled={saving} onClick={save} className="hover:bg-[#014486] h-9 px-4 rounded bg-[#0176D3] text-white text-[13px] font-semibold disabled:opacity-60">{saving ? 'Saving…' : 'Save plan'}</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
